import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock } from 'lucide-react';
import UserLayout from './user/UserLayout';
import { STAUFF_CATALOGUES, CATALOGUES_PATH } from '../data/stauffCatalogues';

const StauffCatalogues = () => {
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              STAUFF Product <span className="text-brand">Catalogues</span>
            </h1>
            <nav className="text-sm text-gray-600 mt-1">
              <button
                onClick={() => history.push('/user/dashboard')}
                className="hover:text-brand transition-colors duration-200"
              >
                Dashboard
              </button>
              <span className="mx-2">{'>'}</span>
              <button
                onClick={() => history.push('/user/product-documents')}
                className="hover:text-brand transition-colors duration-200"
              >
                Product Documents
              </button>
              <span className="mx-2">{'>'}</span>
              <span>STAUFF Catalogues</span>
            </nav>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => history.push('/user/product-documents')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Product Documents
            </button>
          </div>
        </motion.div>

        {/* Catalogue Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {STAUFF_CATALOGUES.map((catalogue) => (
            <CatalogueTile
              key={catalogue.slug}
              catalogue={catalogue}
              isMobile={isMobile}
            />
          ))}
        </motion.div>
      </div>
    </UserLayout>
  );
};

const CatalogueTile = ({ catalogue, isMobile }) => {
  const history = useHistory();
  const { slug, title, description, imageUrl, pdfUrl } = catalogue;
  const available = Boolean(pdfUrl);

  return (
    <motion.div
      whileHover={{ scale: isMobile || !available ? 1 : 1.02 }}
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 ${
        available ? 'cursor-pointer hover:shadow-md hover:border-gray-300' : 'cursor-default'
      }`}
      onClick={() => available && history.push(`${CATALOGUES_PATH}/${slug}`)}
    >
      <div className="relative">
        <img
          src={imageUrl}
          alt={title}
          className={`w-full h-48 object-cover ${available ? '' : 'grayscale opacity-60'}`}
        />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2">
          <BookOpen className="w-4 h-4 text-gray-600" />
        </div>
        {!available && (
          <div className="absolute top-4 left-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-900/80 text-white">
            <Clock className="w-3 h-3 mr-1" />
            Coming soon
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <div className="flex items-center justify-between">
          {available ? (
            <>
              <span className="text-xs text-gray-500">PDF · {catalogue.fileSize}</span>
              <div className="flex items-center text-blue-600 text-sm font-medium">
                View Catalogue
                <motion.div
                  className="ml-1"
                  initial={{ x: 0 }}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  →
                </motion.div>
              </div>
            </>
          ) : (
            <span className="text-xs text-gray-500">Contact our sales team for a copy</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default StauffCatalogues;
